import { Badge } from "@/components/ui/badge";
import { cn } from '@flowstate/utils';

interface MechaClient {
  id: string;
  name: string;
  email: string | null;
  company: string | null;
  industry: string | null;
  lifecycle_stage: string | null;
  status: string | null;
  tier: string | null;
  priority: string | null;
  engagement_score: number | null;
  estimated_value: number | null;
}

interface MechaClientData {
  clients: MechaClient[];
  count: number;
  query_description: string;
}

const TIER_COLORS: Record<string, string> = {
  enterprise: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  premium: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  standard: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  basic: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

function formatValue(n: number | null): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

interface MechaClientBlockProps {
  data: MechaClientData;
}

export function MechaClientBlock({ data }: MechaClientBlockProps) {
  const { clients, count, query_description } = data;

  return (
    <div className="rounded-lg border border-border/50 bg-card/50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/30 bg-muted/30">
        <h3 className="text-sm font-medium text-foreground">{query_description}</h3>
        <Badge variant="outline" className="text-xs">
          {count} {count === 1 ? "client" : "clients"}
        </Badge>
      </div>

      {clients.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          No clients found matching this query.
        </div>
      ) : (
        <div className="divide-y divide-border/20">
          {clients.slice(0, 15).map((client) => (
            <div
              key={client.id}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/20 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{client.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {[client.company, client.email].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              {client.tier && (
                <Badge
                  variant="outline"
                  className={cn("text-[10px] shrink-0", TIER_COLORS[client.tier.toLowerCase()] || "")}
                >
                  {client.tier}
                </Badge>
              )}
              {client.lifecycle_stage && (
                <Badge variant="outline" className="text-[10px] shrink-0">
                  {client.lifecycle_stage.replace(/_/g, " ")}
                </Badge>
              )}
              {client.engagement_score != null && (
                <span className="text-xs text-muted-foreground shrink-0">
                  {client.engagement_score}%
                </span>
              )}
              <span className="text-xs text-muted-foreground shrink-0 w-16 text-right">
                {formatValue(client.estimated_value)}
              </span>
            </div>
          ))}
          {count > 15 && (
            <div className="px-4 py-2 text-xs text-muted-foreground text-center">
              ...and {count - 15} more clients
            </div>
          )}
        </div>
      )}
    </div>
  );
}
